const TARGET_STORAGE_DOC_ID_KEY = 'targetStorageDocId'
const TARGET_STORAGE_NAME_KEY = 'targetStorageName'
const TARGET_STORAGE_EVENT = 'ordnat-target-storage-change'

export type TargetStorageSnapshot = {
    docId: string | null
    name: string | null
}

const EMPTY_SNAPSHOT: TargetStorageSnapshot = { docId: null, name: null }

let cachedSnapshot: TargetStorageSnapshot = EMPTY_SNAPSHOT

function readSnapshot(): TargetStorageSnapshot {
    if (typeof localStorage === 'undefined') return EMPTY_SNAPSHOT

    const docId = localStorage.getItem(TARGET_STORAGE_DOC_ID_KEY)
    const name = localStorage.getItem(TARGET_STORAGE_NAME_KEY)
    if (cachedSnapshot.docId === docId && cachedSnapshot.name === name) {
        return cachedSnapshot
    }

    cachedSnapshot = { docId, name }
    return cachedSnapshot
}

export function getTargetStorageSnapshot(): TargetStorageSnapshot {
    return readSnapshot()
}

export function getTargetStorageDocId(): string | null {
    return readSnapshot().docId
}

export function subscribeTargetStorage(onStoreChange: () => void) {
    const handleChange = () => {
        readSnapshot()
        onStoreChange()
    }

    window.addEventListener(TARGET_STORAGE_EVENT, handleChange)
    window.addEventListener('storage', handleChange)
    return () => {
        window.removeEventListener(TARGET_STORAGE_EVENT, handleChange)
        window.removeEventListener('storage', handleChange)
    }
}

function writeTargetStorage(docId: string, name: string) {
    const current = readSnapshot()
    const nextName = name || (current.docId === docId ? (current.name ?? '') : '')
    if (current.docId === docId && (current.name ?? '') === nextName) return false

    localStorage.setItem(TARGET_STORAGE_DOC_ID_KEY, docId)
    localStorage.setItem(TARGET_STORAGE_NAME_KEY, nextName)
    cachedSnapshot = { docId, name: nextName }
    return true
}

export function setTargetStorage(docId: string, name: string) {
    if (!writeTargetStorage(docId, name)) return
    window.dispatchEvent(new Event(TARGET_STORAGE_EVENT))
}

/** Updates storage focus without notifying subscribers. Used while redirecting old URLs. */
export function setTargetStorageSilently(docId: string, name: string) {
    writeTargetStorage(docId, name)
}

export function clearTargetStorage() {
    const current = readSnapshot()
    if (!current.docId && !current.name) return

    localStorage.removeItem(TARGET_STORAGE_DOC_ID_KEY)
    localStorage.removeItem(TARGET_STORAGE_NAME_KEY)
    cachedSnapshot = EMPTY_SNAPSHOT
    window.dispatchEvent(new Event(TARGET_STORAGE_EVENT))
}
